/** Admin > Overview: the headline figures, anything that needs an admin, who's online and what just happened. */
import { CircleAlert, CircleCheck, Disc3, KeyRound, TriangleAlert, UserPlus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Avatar } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { timeAgo, toast, useSession } from "../lib/app";
import {
  count,
  formatBytes,
  fullHosting,
  fullnessTone,
  money,
  monthName,
  syncProblem,
  URLS,
  useResource,
  type AuditEntry,
  type AuditFacets,
  type AuditPage,
  type HostingResponse,
  type Invite,
  type Resource,
  type StorageReport,
  type SyncResponse,
  type UsersResponse,
} from "./data";
import { InviteDialog } from "./People";
import { RaveDialog } from "./RaveDialog";
import { AdminLink, Card, Loading, Meter, PageHead, Stat, type Tone } from "./ui";

const HOUR = 3_600_000;

/** YYYY-MM-DD in local time, as the audit log's date filters take it. */
const localDate = (t: number) => {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function OverviewPage() {
  const { canManageSync, canRave } = useSession();
  const users = useResource<UsersResponse>(URLS.users);
  const invites = useResource<{ invites: Invite[] }>(URLS.invites);
  const storage = useResource<StorageReport>(URLS.storage);
  const hosting = useResource<HostingResponse>(URLS.hosting);
  const sync = useResource<SyncResponse>(canManageSync ? URLS.sync : null);
  const recent = useResource<AuditPage>("/api/admin/audit?limit=8", 5_000);
  const facets = useResource<AuditFacets>(URLS.facets, 600_000);
  // Rounded to the hour so the address (and the cache entry) stays the same between visits.
  const since = Math.floor((Date.now() - 24 * HOUR) / HOUR) * HOUR;
  const failed = useResource<AuditPage>(`/api/admin/audit?action=auth.login&outcome=failed&from=${since}&limit=200`);
  const [inviting, setInviting] = useState(false);
  const [raving, setRaving] = useState(false);

  const people = users.data?.users ?? [];
  const online = people.filter((u) => u.sockets > 0);
  const openInvites = (invites.data?.invites ?? []).filter((i) => !i.usedAt && i.expiresAt > Date.now()).length;
  const report = storage.data;
  const projects = report?.projects ?? [];
  const live = projects.filter((p) => !p.deletedAt);
  const binned = projects.length - live.length;
  const syncing = sync.data?.projects.filter((p) => p.repo).length ?? 0;
  const disk = report?.disk;
  const fullness = disk?.total ? disk.used / disk.total : 0;
  const h = hosting.data;
  const fly = fullHosting(h);

  return (
    <>
      <PageHead
        title="Overview"
        lede="How benleaf is doing, and anything that needs you."
        actions={
          <button className="btn btn-primary" onClick={() => setInviting(true)}>
            <UserPlus size={15} /> Invite someone
          </button>
        }
      />

      <div className="stats">
        <Stat
          label="People"
          to="/admin/people"
          value={users.data ? people.length : "–"}
          sub={
            users.data
              ? [online.length ? `${online.length} online now` : "Nobody online", openInvites ? `${count(openInvites, "invite link")} open` : ""]
                  .filter(Boolean)
                  .join(" · ")
              : "Loading…"
          }
        />
        <Stat
          label="Projects"
          to="/admin/projects"
          value={report ? live.length : "–"}
          sub={
            report
              ? [
                  canManageSync && sync.data ? `${syncing} syncing` : formatBytes(live.reduce((a, p) => a + p.total, 0)),
                  binned ? `${binned} in recycle bins` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "Loading…"
          }
        />
        <Stat
          label="Disk used"
          to="/admin/storage"
          value={disk ? `${Math.round(fullness * 100)}%` : "–"}
          sub={disk ? `${formatBytes(disk.used)} of ${formatBytes(disk.total)}` : "Measuring…"}
        >
          {disk && <Meter value={fullness} tone={fullnessTone(fullness) as Tone} label="Disk used" />}
        </Stat>
        <Stat
          label={fly ? `Hosting in ${monthName(fly.month.start)}` : "Hosting"}
          to="/admin/hosting"
          value={fly ? money(fly.month.accrued.total) : "–"}
          sub={
            fly ? `So far, of about ${money(fly.month.expected)} for the month` : h ? (h.app ? "Couldn't work out costs" : "Not running on Fly") : "Asking Fly…"
          }
        />
      </div>

      <div className="overview-grid">
        <Attention users={users} storage={storage.data} sync={sync.data} failed={failed.data} hosting={h} />
        <Card
          title="Online now"
          className="online-card"
          actions={
            canRave && (
              <button className="btn btn-sm" onClick={() => setRaving(true)} title="Turn their screens into a disco for 30 seconds">
                <Disc3 size={14} /> Start a rave
              </button>
            )
          }
        >
          {!users.data ? (
            <Loading>Loading…</Loading>
          ) : online.length === 0 ? (
            <p className="admin-empty">Nobody has a project open right now.</p>
          ) : (
            <ul className="online-list">
              {online.map((u) => (
                <li key={u.id}>
                  <span className="person-avatar online">
                    <Avatar id={u.id} name={u.displayName} size={28} />
                  </span>
                  <span className="grow">
                    <b>{u.displayName}</b> <span className="muted">@{u.username}</span>
                  </span>
                  <span className="muted small">{count(u.sockets, "tab")}</span>
                </li>
              ))}
            </ul>
          )}
          {users.data && <p className="online-foot muted small">{recentlyActive(people, online.length > 0)}</p>}
        </Card>
      </div>

      <Card
        title="Recent activity"
        flush
        actions={
          <AdminLink to="/admin/audit" className="btn btn-sm">
            Open audit log
          </AdminLink>
        }
      >
        {!recent.data ? (
          recent.error ? (
            <p className="error-text admin-pad">{recent.error}</p>
          ) : (
            <Loading>Loading activity…</Loading>
          )
        ) : recent.data.entries.length === 0 ? (
          <p className="admin-empty">Nothing has happened yet.</p>
        ) : (
          <ul className="activity">
            {recent.data.entries.map((e) => (
              <ActivityRow key={e.id} e={e} label={facets.data?.actions.find((a) => a.action === e.action)?.label ?? e.action} />
            ))}
          </ul>
        )}
      </Card>

      {raving && <RaveDialog onClose={() => setRaving(false)} />}
      {inviting && (
        <InviteDialog
          onClose={() => {
            setInviting(false);
            invites.reload();
          }}
        />
      )}
    </>
  );
}

/** "3 other people were active in the last day", counting those not online now. */
function recentlyActive(people: UsersResponse["users"], someoneOnline: boolean) {
  const n = people.filter((u) => u.sockets === 0 && u.lastSeen && u.lastSeen > Date.now() - 24 * HOUR).length;
  if (!n) return someoneOnline ? "Nobody else was active in the last day." : "Nobody was active in the last day.";
  return `${someoneOnline ? count(n, "other person", "other people") : count(n, "person", "people")} active in the last day.`;
}

function lowerFirst(s: string) {
  return s ? s[0].toLowerCase() + s.slice(1) : s;
}

function ActivityRow({ e, label }: { e: AuditEntry; label: string }) {
  const failedLogin = !e.ok && e.action === "auth.login";
  return (
    <li className={`activity-row${e.ok ? "" : " failed"}`}>
      <span className="activity-when muted" title={new Date(e.at).toLocaleString()}>
        {timeAgo(e.at)}
      </span>
      <span className="activity-what">
        {failedLogin ? (
          <>
            <b>Failed sign-in</b>
            {e.targetUsername && <> as {e.targetUsername}</>}
          </>
        ) : (
          <>
            {/* Signing in or up happens before there's a user, so the log names them as the target. */}
            <b>{e.username || e.targetUsername || (e.ip ? "Someone" : "The server")}</b> {lowerFirst(label)}
            {e.username && e.targetUsername && e.targetUsername !== e.username && <span className="muted"> · @{e.targetUsername}</span>}
          </>
        )}
        {e.projectName && <span className="muted"> · {e.projectName}</span>}
      </span>
      <span className="activity-where muted mono">{e.ip}</span>
      {!e.ok && <span className="chip chip-bad">{e.status === 401 || e.status === 403 ? "refused" : "failed"}</span>}
    </li>
  );
}

interface Item {
  key: string;
  tone: Tone | "info";
  text: ReactNode;
  action?: ReactNode;
}

/** Everything an admin should look at, most urgent first. Empty most days. */
function Attention({
  users,
  storage,
  sync,
  failed,
  hosting,
}: {
  users: Resource<UsersResponse>;
  storage: StorageReport | undefined;
  sync: SyncResponse | undefined;
  failed: AuditPage | undefined;
  hosting: HostingResponse | undefined;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const items: Item[] = [];

  async function act(key: string, url: string, done: string) {
    setBusy(key);
    try {
      await api.post(url);
      toast(done, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    await users.reload();
    setBusy(null);
  }

  const disk = storage?.disk;
  const fullness = disk?.total ? disk.used / disk.total : 0;
  if (disk && fullness >= 0.8)
    items.push({
      key: "disk",
      tone: fullnessTone(fullness) as Tone,
      text: (
        <>
          The disk is <b>{Math.round(fullness * 100)}% full</b>, with {formatBytes(disk.free)} left. Clear compile caches or grow the volume before it runs out.
        </>
      ),
      action: (
        <AdminLink to="/admin/storage" className="btn btn-sm">
          Open storage
        </AdminLink>
      ),
    });

  for (const p of sync?.projects.filter(syncProblem) ?? [])
    items.push({
      key: `sync-${p.id}`,
      tone: "bad",
      text: (
        <>
          Sync is failing for <b>{p.name}</b>
          {p.syncState.lastError ? <span className="muted">: {p.syncState.lastError}</span> : "."}
        </>
      ),
      action: (
        <AdminLink to={`/admin/projects?show=${p.id}`} className="btn btn-sm">
          Details
        </AdminLink>
      ),
    });

  const people = users.data?.users ?? [];
  const nameOf = (id: number | null) => people.find((u) => u.id === id)?.username;
  const unverified = people.filter((u) => !u.verified && !u.isAdmin);
  if (unverified.length > 3)
    items.push({
      key: "unverified",
      tone: "warn",
      text: (
        <>
          <b>{unverified.length} people</b> are waiting to be verified before they can create projects.
        </>
      ),
      action: (
        <AdminLink to="/admin/people" className="btn btn-sm">
          Review
        </AdminLink>
      ),
    });
  else
    for (const u of unverified)
      items.push({
        key: `verify-${u.id}`,
        tone: "warn",
        text: (
          <>
            <b>{u.displayName}</b> can't create projects until you verify them
            {nameOf(u.invitedBy) ? <span className="muted"> · added by @{nameOf(u.invitedBy)}</span> : "."}
          </>
        ),
        action: (
          <button
            className="btn btn-sm btn-primary"
            disabled={busy === `verify-${u.id}`}
            onClick={() => act(`verify-${u.id}`, `/api/admin/users/${u.id}/verify`, `${u.displayName} can now create projects`)}
          >
            Verify
          </button>
        ),
      });

  const limit = users.data?.inviteLimit ?? 0;
  for (const u of people.filter((u) => u.isTrusted && !u.isAdmin && limit > 0 && (u.invitesCounted ?? 0) >= limit))
    items.push({
      key: `limit-${u.id}`,
      tone: "info",
      text: (
        <>
          <b>{u.displayName}</b> has used all {limit} of their invites.
        </>
      ),
      action: (
        <button
          className="btn btn-sm"
          disabled={busy === `limit-${u.id}`}
          onClick={() => act(`limit-${u.id}`, `/api/admin/users/${u.id}/invite-limit/reset`, `${u.displayName} can invite ${limit} more people`)}
        >
          Reset
        </button>
      ),
    });

  const failures = failed?.entries.length ?? 0;
  if (failures >= 3) {
    const names = [...new Set(failed!.entries.map((e) => e.targetUsername).filter(Boolean))];
    const yesterday = localDate(Date.now() - 24 * HOUR);
    items.push({
      key: "logins",
      tone: failures >= 20 ? "bad" : "warn",
      text: (
        <>
          <b>{failed!.next ? `More than ${failures}` : failures} failed sign-ins</b> in the last day
          {names.length ? (
            <span className="muted">
              {" "}
              · as {names.slice(0, 3).join(", ")}
              {names.length > 3 ? ` and ${names.length - 3} more` : ""}
            </span>
          ) : (
            "."
          )}
        </>
      ),
      action: (
        <AdminLink to={`/admin/audit?action=auth.login&outcome=failed&from=${yesterday}`} className="btn btn-sm">
          See them
        </AdminLink>
      ),
    });
  }

  const fly = fullHosting(hosting);
  if (fly?.flyError)
    items.push({
      key: "fly",
      tone: "warn",
      text: (
        <>
          Couldn't reach Fly<span className="muted">: {fly.flyError}</span>
        </>
      ),
      action: (
        <AdminLink to="/admin/hosting" className="btn btn-sm">
          Hosting
        </AdminLink>
      ),
    });

  const order: Record<Item["tone"], number> = { bad: 0, warn: 1, ok: 2, info: 3 };
  items.sort((a, b) => order[a.tone] - order[b.tone]);
  const loading = !users.data || !storage;

  return (
    <Card title="Needs attention" className="attention-card" sub={items.length ? count(items.length, "thing") : undefined}>
      {items.length === 0 ? (
        loading ? (
          <Loading>Checking…</Loading>
        ) : (
          <p className="attention-clear">
            <CircleCheck size={18} /> Nothing needs you right now.
          </p>
        )
      ) : (
        <ul className="attention">
          {items.map((i) => (
            <li key={i.key} className={`attention-item tone-${i.tone}`}>
              <span className="attention-icon" aria-hidden="true">
                {i.tone === "bad" ? <CircleAlert size={16} /> : i.tone === "warn" ? <TriangleAlert size={16} /> : <KeyRound size={16} />}
              </span>
              <span className="attention-text">{i.text}</span>
              {i.action}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
